/**
 * Helpers for GET /v1/shop/catalog/ entries (synthetic list keys vs integer API ids).
 */

/**
 * @param {Object|null|undefined} entry
 * @returns {boolean}
 */
export function isShopCatalogEntry(entry) {
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  if (entry.normalized_cat_num != null) {
    return true;
  }
  const id = entry.id;
  return typeof id === 'string' && id.startsWith('shop:');
}

/**
 * React list key for shop catalog rows.
 *
 * @param {Object|null|undefined} entry
 * @returns {string|number|null}
 */
export function getShopListKey(entry) {
  if (!entry) return null;
  if (entry.id != null) return entry.id;
  if (entry.normalized_cat_num != null) return `shop:${entry.normalized_cat_num}`;
  if (entry.cat_num != null) return `shop:${entry.cat_num}`;
  return null;
}

/**
 * Integer product id for cart, quotes, and GET /products/{id}/get_details/.
 *
 * @param {Object|null|undefined} entry
 * @returns {number|null}
 */
export function getShopProductApiId(entry) {
  if (!entry) return null;
  const primary = entry.primary_product_id;
  if (Number.isInteger(primary) && primary > 0) {
    return primary;
  }
  const rawId = entry.id;
  if (typeof rawId === 'number' && Number.isInteger(rawId) && rawId > 0) {
    return rawId;
  }
  if (typeof rawId === 'string' && /^\d+$/.test(rawId)) {
    return Number(rawId);
  }
  return null;
}

/**
 * Map shop catalog entry to product-like shape for existing display components.
 *
 * @param {Object} entry
 * @returns {Object}
 */
export function normalizeShopCatalogEntry(entry) {
  if (!entry) return entry;
  return {
    ...entry,
    supplier: entry.supplier ?? entry.supplier_id ?? null,
    supplier_name: entry.supplier_name ?? entry.supplier?.name ?? '',
    manufacturer_name: entry.manufacturer_name ?? entry.manufacturer?.name ?? '',
  };
}

/**
 * Client-side filter for shop catalog (v1 has no server pagination).
 *
 * @param {Array<Object>} entries
 * @param {Object} [options]
 * @param {string} [options.search]
 * @param {string} [options.supplierCatalogue] - '', 'true', or 'false' (supplier_cat_item filter)
 * @param {string|number} [options.supplierId] - Filter by supplier_id
 * @returns {Array<Object>}
 */
export function filterShopCatalog(entries, options = {}) {
  const list = Array.isArray(entries) ? entries : [];
  const search = String(options.search ?? '')
    .trim()
    .toLowerCase();
  const catalogueFilter = String(options.supplierCatalogue ?? '')
    .trim()
    .toLowerCase();
  const supplierId = String(options.supplierId ?? '').trim();

  return list.filter(entry => {
    if (supplierId && String(entry.supplier_id ?? entry.supplier ?? '') !== supplierId) {
      return false;
    }
    if (catalogueFilter === 'true' && entry.supplier_cat_item !== true) {
      return false;
    }
    if (catalogueFilter === 'false' && entry.supplier_cat_item !== false) {
      return false;
    }
    if (!search) {
      return true;
    }
    const haystack = [
      entry.name,
      entry.cat_num,
      entry.normalized_cat_num,
      entry.manufacturer_name,
      entry.supplier_name,
      entry.category,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(search);
  });
}

/**
 * Normalize and filter shop catalog entries for ProductList.
 *
 * @param {Array<Object>} entries
 * @param {Object} [filterOptions]
 * @returns {Array<Object>}
 */
export function prepareShopCatalogForList(entries, filterOptions = {}) {
  return filterShopCatalog(
    (entries || []).map(normalizeShopCatalogEntry),
    filterOptions
  );
}
